/**
 * One week of one run, the Week board read only (`Week.dc.html`, canvas PR
 * #60): the week at a glance, then per day the trips per vehicle, one duty per
 * own driver against the aim and the limit, and the day's findings counted,
 * each day a way into its day page.
 *
 * A week is named by its Monday and shows the days of that Monday to Sunday
 * inside the run's horizon. The board's "Compare with published" and "Review
 * and publish" are not here: the first UI is read only (spec D4).
 */
import {
  EmptyState,
  Explainer,
  PageHeader,
  Section,
  Stat,
  StatGroup,
  type PagePeriod,
} from "@depicomnlbv/platform-ui";
import { Link, useParams } from "react-router-dom";

import { useRuns, useWeek, type FindingCount, type Run, type Week as WeekData } from "../api";
import { Failure, Pending } from "../components/Failure";
import { PlanSwitch } from "../components/PlanSwitch";
import { weekAxis, WeekTimeline, type Axis } from "../components/WeekTimeline";
import { runName } from "../runs";
import {
  addDays,
  formatDate,
  formatLitres,
  formatNumber,
  formatShortDate,
  isCalendarDate,
  mondayOf,
  zoneName,
} from "../time";
import { vehicleRows } from "../trips";
import { useTitle } from "../useTitle";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "1 hard finding, none to look at": severity in words, never colour alone. */
export function findingsText({ hard, warn }: FindingCount): string {
  if (hard === 0 && warn === 0) return "No findings";
  const h = hard === 0 ? "no hard findings" : plural(hard, "hard finding", "hard findings");
  const w = warn === 0 ? "none to look at" : `${warn} to look at`;
  return `${h.charAt(0).toUpperCase()}${h.slice(1)}, ${w}`;
}

/** The previous and next week, as the page header's period, never past the
 *  horizon. Each link shows the Monday and is named in full. */
function weekPeriod(run: Run, monday: string): PagePeriod {
  const previous = addDays(monday, -7);
  const next = addDays(monday, 7);
  const base = `/runs/${run.run_id}/weeks`;
  const first = mondayOf(run.horizon_start);
  const weekNumber = (m: string) => Math.round((Date.parse(m) - Date.parse(first)) / (7 * 86_400_000)) + 1;
  return {
    label: "Weeks of this run",
    current: `Week ${weekNumber(monday)} of ${weekNumber(mondayOf(run.horizon_end))}`,
    previous:
      addDays(previous, 6) >= run.horizon_start ? (
        <Link aria-label={`Previous week, ${formatShortDate(previous)}`} rel="prev" to={`${base}/${previous}`}>
          {formatShortDate(previous)}
        </Link>
      ) : undefined,
    next:
      next <= run.horizon_end ? (
        <Link aria-label={`Next week, ${formatShortDate(next)}`} rel="next" to={`${base}/${next}`}>
          {formatShortDate(next)}
        </Link>
      ) : undefined,
  };
}

function Glance({ week }: { week: WeekData }) {
  const trips = week.days.flatMap((d) => d.trips);
  const litres = trips.reduce((sum, t) => sum + (t.litres ?? 0), 0);
  const dated = week.days.reduce(
    (sum, d) => ({ hard: sum.hard + d.findings.hard, warn: sum.warn + d.findings.warn }),
    { hard: 0, warn: 0 },
  );
  const undated = week.undated_findings.hard + week.undated_findings.warn;
  const hard = dated.hard + week.undated_findings.hard;
  const warn = dated.warn + week.undated_findings.warn;
  const longest = Math.max(0, ...week.days.flatMap((d) => d.duties.map((x) => x.hours)));
  const hasDuty = week.days.some((d) => d.duties.length > 0);

  // Four tiles of one value each (the Stat block): a label, the number with
  // its unit, at most one line under it.
  return (
    <StatGroup>
      <Stat label="Trips" value={formatNumber(trips.length)} />
      <Stat label="Planned" unit="L" value={formatNumber(litres)} />
      <Stat
        context={
          undated > 0 ? `${warn} to look at, ${undated} on no single day` : `${warn} to look at`
        }
        label="Hard findings"
        value={formatNumber(hard)}
      />
      {hasDuty ? (
        <Stat
          context={`aim ${week.duty_aim_hours} h, limit ${week.duty_limit_hours} h`}
          label="Longest duty"
          unit="h"
          value={longest.toFixed(1)}
        />
      ) : (
        <Stat label="Longest duty" unavailable="No own driver's duty this week" value={null} />
      )}
    </StatGroup>
  );
}

function WeekPlan({ week, axis }: { week: WeekData; axis: Axis }) {
  const undated = week.undated_findings.hard + week.undated_findings.warn;
  return (
    <>
      <Glance week={week} />
      {/* One Timeline section, as the Week board draws it: each day a group
          named by its heading. A day with trips has its timeline under it; a
          day without trips is one row, not a card (UI review, finding 4). */}
      <Section empty={week.days.length === 0 ? "No days of the run in this week." : undefined} title="Timeline">
        <div className="week-days">
          {week.days.map((day) => {
            const heading = formatDate(day.date);
            const id = `week-day-${day.date}`;
            const vehicles = vehicleRows(day.trips).length;
            const litres = day.trips.reduce((sum, t) => sum + (t.litres ?? 0), 0);
            const empty = day.trips.length === 0;
            return (
              <div
                aria-labelledby={id}
                className={empty ? "week-day week-day--empty" : "week-day"}
                key={day.date}
                role="group"
              >
                <div className="week-day__head">
                  <h4 className="week-day__title" id={id}>
                    {heading}
                  </h4>
                  <span className="week-day__summary">
                    {empty
                      ? "No trips planned"
                      : `${plural(day.trips.length, "trip", "trips")} · ${plural(vehicles, "vehicle", "vehicles")} · ${formatLitres(litres)}`}
                  </span>
                  <span className="week-day__findings">{findingsText(day.findings)}</span>
                  <Link
                    // The visible words first, then the day (WCAG 2.5.3, label in name).
                    aria-label={`Open day plan, ${heading}`}
                    className="week-day__open"
                    to={`/runs/${week.run_id}/days/${day.date}`}
                  >
                    Open day plan
                  </Link>
                </div>
                {empty ? null : <WeekTimeline axis={axis} day={day} week={week} />}
              </div>
            );
          })}
        </div>
      </Section>
      <Explainer summary="How to read this week">
        <p>Times are in {zoneName(week.time_zone)}.</p>
        <p>
          Each bar runs from departure to back at factory; circles are stops. A duty runs from the
          pre-trip check ({week.pre_trip_min} min) to the last return, and is measured against the
          aim of {week.duty_aim_hours} h and the limit of {week.duty_limit_hours} h.
        </p>
        <p>
          Hard findings counts every day of the week and the findings that belong to no single day,
          a farm&apos;s gap between collections or the whole run.
          {undated > 0
            ? ` ${plural(undated, "finding belongs", "findings belong")} to no single day this week (${week.undated_findings.hard} hard, ${week.undated_findings.warn} to look at); every day page lists ${undated === 1 ? "it" : "them"}.`
            : ""}
        </p>
      </Explainer>
    </>
  );
}

export function Week() {
  const params = useParams();
  const date = params.monday;
  const valid = /^\d+$/.test(params.runId ?? "") && isCalendarDate(date);
  const isMonday = valid && mondayOf(date) === date;
  const runId = valid ? Number(params.runId) : 0;

  useTitle(isMonday ? `Week of ${formatShortDate(date)}, run ${runId}` : "Not a week");
  const runs = useRuns();
  const week = useWeek(runId, date ?? "", isMonday);

  if (!isMonday) {
    return (
      <>
        <PageHeader actions={<Link to="/runs">All runs</Link>} title="Not a week" />
        <EmptyState tone="critical">
          {valid ? (
            <>
              {formatDate(date)} is not a Monday, and a week is named by its Monday: this one is{" "}
              {formatDate(mondayOf(date))}.{" "}
              <Link to={`/runs/${runId}/weeks/${mondayOf(date)}`}>
                The week of {formatShortDate(mondayOf(date))}
              </Link>
            </>
          ) : (
            "This address does not name a week of a run. Open a run from the list, then its week."
          )}
        </EmptyState>
      </>
    );
  }

  const run = runs.data?.runs.find((r) => r.run_id === runId);
  // The day the switch opens: the week's first day inside the horizon. Until
  // the week or the run says which that is, there is no Day link, rather than
  // one that may point at a day before the horizon.
  const firstDay =
    week.data?.days[0]?.date ?? (run ? (date < run.horizon_start ? run.horizon_start : date) : null);

  const qualifier = [run ? runName(run) : null, week.data ? zoneName(week.data.time_zone) : null].filter(
    (part) => part !== null,
  );

  return (
    <>
      <PageHeader
        actions={
          <>
            {firstDay ? (
              <PlanSwitch
                current="week"
                dayTo={`/runs/${runId}/days/${firstDay}`}
                weekTo={`/runs/${runId}/weeks/${date}`}
              />
            ) : null}
            <Link to="/runs">All runs</Link>
          </>
        }
        // A week outside the horizon is refused by the API and has no number
        // in the run, so it is not given one.
        period={run && addDays(date, 6) >= run.horizon_start && date <= run.horizon_end ? weekPeriod(run, date) : undefined}
        qualifier={qualifier.length > 0 ? qualifier.join(" · ") : undefined}
        title={`Week of ${formatDate(date)}`}
      />
      {week.isPending ? (
        <Pending>Reading the week</Pending>
      ) : week.isError ? (
        <Failure context={{ runId, monday: date }} error={week.error} />
      ) : (
        <WeekPlan axis={weekAxis(week.data)} week={week.data} />
      )}
    </>
  );
}
